import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchPhysicalCardsData,
  fetchPhysicalTableData,
  fetchPhysicalFilterDimensions,
} from "../api/physical_view_endpoints";
import { isSupersededResponse } from "../utils/isSupersededResponse";

// Label mapping for metrics
const METRIC_LABEL_MAP = {
  pct_availability: "My Availability",
  pct_availability_across_dsf_w_bev: "W/Drinks",
  pct_core_availability: "Full Core Av.",
  pct_competitor_availability: "Competitor Av.",
  pct_combo: "Combo",
  pct_name_accuracy: "Name",
  pct_image_availability: "Image",
  pct_correct_image: "Correct Image",
};

// Async thunk for fetching cards data
export const fetchPhysicalCards = createAsyncThunk(
  "physicalView/fetchCards",
  async ({ id_client, country, filters, searchTerm }, { rejectWithValue }) => {
    try {
      const response = await fetchPhysicalCardsData({
        id_client,
        country,
        filters,
        searchTerm,
      });

      // Transform the response to match component expectations. A platform
      // card keeps its raw `id_platform` code (`card.title`, e.g. "JSE");
      // the page resolves the display name at render time, so a name never
      // freezes as a code when metadata arrives after the data.
      const transformedCards =
        response.cards?.map((card) => {
          const isTotal = card.title === "Total";

          if (isTotal) {
            // Handle "Total" card with different field names
            return {
              name: "Aggregated",
              count: card.total_nof_outlet,
              dashoneScore: card.total_score,
              deltaScore: card.total_delta_score,
              metrics: [
                {
                  label: METRIC_LABEL_MAP.pct_availability_across_dsf_w_bev,
                  value: card.total_nof_outlet_w_drink,
                  percentage: card.total_pct_outlet_w_drink,
                  trend: card.total_delta_pct_outlet_w_drink,
                },
                {
                  label: METRIC_LABEL_MAP.pct_availability,
                  value: card.total_nof_outlet_w_client_bev,
                  percentage: card.total_pct_availability_across_dsf_w_bev,
                  trend: card.total_delta_pct_availability_across_dsf_w_bev,
                },
                {
                  label: METRIC_LABEL_MAP.pct_core_availability,
                  value: card.total_nof_outlet_w_core_brand,
                  percentage: card.total_pct_core_availability,
                  trend: card.total_delta_pct_core_availability,
                },
                {
                  label: METRIC_LABEL_MAP.pct_competitor_availability,
                  value: card.total_nof_outlet_w_competitor_bev,
                  percentage:
                    card.total_pct_competitor_availability_across_dsf_w_bev,
                  trend:
                    card.total_delta_pct_competitor_availability_across_dsf_w_bev,
                },
                {
                  label: METRIC_LABEL_MAP.pct_combo,
                  value: card.total_nof_outlet_w_client_combo,
                  percentage: card.total_pct_combo,
                  trend: card.total_delta_pct_combo,
                },
                {
                  label: METRIC_LABEL_MAP.pct_image_availability,
                  value: card.total_nof_outlet_w_image_availability,
                  percentage: card.total_pct_image_availability,
                  trend: card.total_delta_pct_image_availability,
                },
                {
                  label: METRIC_LABEL_MAP.pct_correct_image,
                  value: card.total_nof_outlet_w_correct_image,
                  percentage: card.total_pct_correct_image,
                  trend: card.total_delta_pct_correct_image,
                },
                {
                  label: METRIC_LABEL_MAP.pct_name_accuracy,
                  value: card.total_nof_outlet_w_name_accuracy,
                  percentage: card.total_pct_name_accuracy,
                  trend: card.total_delta_pct_name_accuracy,
                },
              ],
            };
          } else {
            // Handle platform cards (UBE, TKW, WOL) with "_map" suffix fields
            return {
              platformCode: card.title,
              count: card.total_platform_nof_outlet_map,
              dashoneScore: card.total_platform_score_map,
              deltaScore: card.total_platform_delta_score_map,
              metrics: [
                {
                  label: METRIC_LABEL_MAP.pct_availability_across_dsf_w_bev,
                  value: card.total_platform_nof_outlet_w_drink_map,
                  percentage: card.total_platform_pct_outlet_w_drink_map,
                  trend: card.total_platform_delta_pct_outlet_w_drink_map,
                },
                {
                  label: METRIC_LABEL_MAP.pct_availability,
                  value: card.total_platform_nof_outlet_w_client_bev_map,
                  percentage:
                    card.total_platform_pct_availability_across_dsf_w_bev_map,
                  trend:
                    card.total_platform_delta_pct_availability_across_dsf_w_bev_map,
                },
                {
                  label: METRIC_LABEL_MAP.pct_core_availability,
                  value: card.total_platform_nof_outlet_w_core_brand_map,
                  percentage: card.total_platform_pct_core_availability_map,
                  trend: card.total_platform_delta_pct_core_availability_map,
                },
                {
                  label: METRIC_LABEL_MAP.pct_competitor_availability,
                  value: card.total_platform_nof_outlet_w_competitor_bev_map,
                  percentage:
                    card.total_platform_pct_competitor_availability_across_dsf_w_bev_map,
                  trend:
                    card.total_platform_delta_pct_competitor_availability_across_dsf_w_bev_map,
                },
                {
                  label: METRIC_LABEL_MAP.pct_combo,
                  value: card.total_platform_nof_outlet_w_client_combo_map,
                  percentage: card.total_platform_pct_combo_map,
                  trend: card.total_platform_delta_pct_combo_map,
                },
                {
                  label: METRIC_LABEL_MAP.pct_image_availability,
                  value:
                    card.total_platform_nof_outlet_w_image_availability_map,
                  percentage: card.total_platform_pct_image_availability_map,
                  trend: card.total_platform_delta_pct_image_availability_map,
                },
                {
                  label: METRIC_LABEL_MAP.pct_correct_image,
                  value: card.total_platform_nof_outlet_w_correct_image_map,
                  percentage: card.total_platform_pct_correct_image_map,
                  trend: card.total_platform_delta_pct_correct_image_map,
                },
                {
                  label: METRIC_LABEL_MAP.pct_name_accuracy,
                  value: card.total_platform_nof_outlet_w_name_accuracy_map,
                  percentage: card.total_platform_pct_name_accuracy_map,
                  trend: card.total_platform_delta_pct_name_accuracy_map,
                },
              ],
            };
          }
        }) || [];

      // Sort cards: Aggregated first, then by count (total_nof_outlet) descending
      transformedCards.sort((a, b) => {
        // Always put Aggregated first
        if (a.name === "Aggregated") return -1;
        if (b.name === "Aggregated") return 1;

        // Sort by count (total_nof_outlet) in descending order
        const countA = a.count ?? 0;
        const countB = b.count ?? 0;
        return countB - countA;
      });

      return { cards: transformedCards };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Async thunk for fetching table data
export const fetchPhysicalTable = createAsyncThunk(
  "physicalView/fetchTable",
  async (
    { id_client, country, page = 1, limit = 50, filters, sorter, searchTerm },
    { rejectWithValue }
  ) => {
    try {
      const response = await fetchPhysicalTableData({
        id_client,
        country,
        page,
        limit,
        filters,
        sorter,
        searchTerm,
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


const initialState = {
  // Cards data
  cards: [],
  cardsLoading: false,
  cardsError: null,
  latestCardsRequestId: null,

  // Table data
  tableData: [],
  latestTableRequestId: null,
  coreBrandMapping: {},
  total: 0, // Total count for pagination
  latestRefresh: null, // Latest refresh date from API
  tableLoading: false,
  tableError: null,

  // Filter dimensions (from Physical Location-specific endpoint)
  filterDimensions: {},
  latestDimensionsRequestId: null,
  dimensionsLoading: false,
  dimensionsError: null,

  // UI state - unified loading state
  firstLoading: true,
  isDataLoading: false,
};

export const fetchPhysicalDimensions = createAsyncThunk(
  "physicalView/fetchDimensions",
  async ({ id_client, country }, { rejectWithValue }) => {
    try {
      return await fetchPhysicalFilterDimensions({ id_client, country });
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const physicalViewSlice = createSlice({
  name: "physicalView",
  initialState,
  reducers: {
    resetPhysicalView: (state) => {
      state.cards = [];
      state.tableData = [];
      state.coreBrandMapping = {};
      state.total = 0;
      state.cardsLoading = false;
      state.tableLoading = false;
      state.cardsError = null;
      state.tableError = null;
      state.firstLoading = true;
      state.isDataLoading = false;
    },
    // New action to set loading state at the start of fetch sequence
    setDataLoading: (state, action) => {
      state.isDataLoading = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch cards
      .addCase(fetchPhysicalCards.pending, (state, action) => {
        state.latestCardsRequestId = action.meta?.requestId ?? null;
        state.cardsLoading = true;
        state.cardsError = null;
        // Don't clear old data - keep showing previous data while loading
      })
      .addCase(fetchPhysicalCards.fulfilled, (state, action) => {
        if (isSupersededResponse(state.latestCardsRequestId, action)) return;
        state.cardsLoading = false;
        // An empty result means no outlet matched the filters. Render that as
        // empty rather than substituting cards, which the table's platform
        // column groups are also derived from.
        state.cards = action.payload.cards || [];
        // Don't set firstLoading here - wait for all data
      })
      .addCase(fetchPhysicalCards.rejected, (state, action) => {
        if (isSupersededResponse(state.latestCardsRequestId, action)) return;
        state.cardsLoading = false;
        state.cardsError = action.payload;
        state.cards = [];
        // Don't set firstLoading/isDataLoading here - let table data control it
      })
      .addCase(fetchPhysicalTable.pending, (state, action) => {
        state.latestTableRequestId = action.meta?.requestId ?? null;
        state.tableLoading = true;
        state.tableError = null;
        // Don't clear old data - keep showing previous data while loading
      })
      .addCase(fetchPhysicalTable.fulfilled, (state, action) => {
        if (isSupersededResponse(state.latestTableRequestId, action)) return;
        state.tableLoading = false;

        state.tableData = action.payload.data || [];
        state.coreBrandMapping = action.payload.core_brand_mapping || {};
        state.total = action.payload.total || 0;
        state.latestRefresh = action.payload.latest_refresh || null;
        state.firstLoading = false;
        state.isDataLoading = false; // All data loaded
      })
      .addCase(fetchPhysicalTable.rejected, (state, action) => {
        if (isSupersededResponse(state.latestTableRequestId, action)) return;
        state.tableLoading = false;
        state.tableError = action.payload;
        state.tableData = [];
        state.coreBrandMapping = {};
        state.total = 0;
        state.firstLoading = false;
        state.isDataLoading = false;
      })
      // Fetch filter dimensions
      .addCase(fetchPhysicalDimensions.pending, (state, action) => {
        state.latestDimensionsRequestId = action.meta?.requestId ?? null;
        state.dimensionsLoading = true;
        state.dimensionsError = null;
      })
      .addCase(fetchPhysicalDimensions.fulfilled, (state, action) => {
        if (isSupersededResponse(state.latestDimensionsRequestId, action)) return;
        state.dimensionsLoading = false;
        state.filterDimensions = action.payload || {};
      })
      .addCase(fetchPhysicalDimensions.rejected, (state, action) => {
        if (isSupersededResponse(state.latestDimensionsRequestId, action)) return;
        state.dimensionsLoading = false;
        state.dimensionsError = action.payload;
        state.filterDimensions = {};
      })
  },
});

export const { resetPhysicalView, setDataLoading } = physicalViewSlice.actions;
export const selectPhysicalFilterDimensions = (state) => state.physicalView.filterDimensions;

export default physicalViewSlice.reducer;
